import React from 'react';
import { Car, Check, Fuel } from 'lucide-react';
import ModelThumb from '../ModelThumb';
import { useT } from '../../services/i18n';
import { DropdownSearch } from './VehicleDropdown';
import { getFuelColor, getFuelLabel } from './useVehicleData';

const optionClass = (active) => `w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 hover:text-orange-600 transition-colors flex items-center justify-between ${active ? 'bg-orange-50 text-orange-600' : ''}`;

export const BrandOptions = ({ brands, brandLogos, popularBrands, selected, search, setSearch, onSelect }) => {
  const { t } = useT();
  const filtered = search ? brands.filter(b => b.toLowerCase().includes(search.toLowerCase())) : brands;
  return (
    <>
      <DropdownSearch testId="vehicle-brand-search" value={search} onChange={setSearch} placeholder={t('home.search')} />
      <div className="max-h-72 overflow-y-auto">
        {!search && popularBrands.length > 0 && (
          <div className="p-2 border-b border-gray-100" data-testid="vehicle-popular-brands">
            <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{t('home.topBrands')}</p>
            <div className="grid grid-cols-5 gap-1">
              {popularBrands.map((brand) => (
                <button key={brand} type="button" title={brand} data-testid={`vehicle-popular-brand-${brand}`}
                  onClick={(e) => { e.stopPropagation(); onSelect(brand); }}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-1.5 transition-colors hover:border-orange-400 hover:bg-orange-50 ${selected === brand ? 'border-orange-500 bg-orange-50' : 'border-gray-100'}`}>
                  <span className="w-10 h-10 flex items-center justify-center">
                    {brandLogos[brand] ? <img src={brandLogos[brand]} alt={brand} className="max-w-full max-h-full object-contain" /> : <Car size={20} className="text-gray-300" />}
                  </span>
                  <span className="text-[9px] font-medium text-gray-600 truncate w-full text-center">{brand}</span>
                </button>
              ))}
            </div>
            <p className="px-2 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{t('home.allBrands')}</p>
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 text-center">{t('home.noBrand')}</div>
        ) : (
          filtered.map((brand) => (
            <button key={brand} type="button" data-testid={`vehicle-brand-option-${brand}`} onClick={(e) => { e.stopPropagation(); onSelect(brand); }} className={optionClass(selected === brand)}>
              <span className="flex items-center gap-2.5 min-w-0">
                <span className="w-7 h-7 rounded bg-white border border-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                  {brandLogos[brand] ? <img src={brandLogos[brand]} alt="" loading="lazy" className="w-6 h-6 object-contain" data-testid={`vehicle-brand-logo-${brand}`} /> : <Car size={14} className="text-gray-300" />}
                </span>
                <span className="truncate">{brand}</span>
              </span>
              {selected === brand && <Check size={16} className="text-orange-500" />}
            </button>
          ))
        )}
      </div>
    </>
  );
};

export const ModelOptions = ({ brand, models, modelImages, selected, search, setSearch, onSelect }) => {
  const { t } = useT();
  const filtered = search ? models.filter(m => m.toLowerCase().includes(search.toLowerCase())) : models;
  const gallery = filtered.filter((x) => modelImages[x]).map((x) => ({ src: modelImages[x], alt: `${brand} ${x}` }));
  return (
    <>
      <DropdownSearch testId="vehicle-model-search" value={search} onChange={setSearch} placeholder={t('home.search')} />
      <div className="max-h-56 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 text-center">{t('home.noModel')}</div>
        ) : (
          filtered.map((model) => (
            <button key={model} type="button" data-testid={`vehicle-model-option-${model}`} onClick={(e) => { e.stopPropagation(); onSelect(model); }} className={optionClass(selected === model)}>
              <span className="flex items-center gap-2 min-w-0">
                {modelImages[model] && <ModelThumb src={modelImages[model]} alt={`${brand} ${model}`} size={36} testId={`vehicle-model-option-image-${model}`} gallery={gallery} />}
                <span className="truncate">{model}</span>
              </span>
              {selected === model && <Check size={16} className="text-orange-500" />}
            </button>
          ))
        )}
      </div>
    </>
  );
};

export const YearOptions = ({ years, selected, onSelect }) => {
  const { t } = useT();
  return (
    <div className="max-h-56 overflow-y-auto">
      {years.length === 0 ? (
        <div className="p-4 text-sm text-gray-500 text-center">{t('home.selectModel')}</div>
      ) : (
        years.map((year) => (
          <button key={year} type="button" data-testid={`vehicle-year-option-${year}`} onClick={(e) => { e.stopPropagation(); onSelect(year.toString()); }} className={optionClass(selected === year.toString())}>
            {year}
            {selected === year.toString() && <Check size={16} className="text-orange-500" />}
          </button>
        ))
      )}
    </div>
  );
};

export const EngineOptions = ({ engines, selectedYear, selected, onSelect }) => {
  const { t } = useT();
  const byFuel = engines.reduce((acc, e) => { (acc[e.fuel] = acc[e.fuel] || []).push(e); return acc; }, {});
  return (
    <div className="max-h-72 overflow-y-auto">
      {engines.length === 0 ? (
        <div className="p-4 text-sm text-gray-500 text-center">{selectedYear ? t('home.noEngine') : t('home.selectYear')}</div>
      ) : (
        Object.entries(byFuel).map(([fuel, fuelEngines]) => (
          <div key={fuel}>
            <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide sticky top-0 flex items-center gap-2 border-y border-white/60 ${getFuelColor(fuel)}`} data-testid={`vehicle-fuel-group-${fuel}`}>
              <Fuel size={14} />
              {getFuelLabel(fuel)}
              <span className="opacity-60 font-normal">({fuelEngines.length})</span>
            </div>
            {fuelEngines.map((engine, idx) => (
              <button key={`${fuel}-${idx}`} type="button" data-testid={`vehicle-engine-option-${engine.name}`} onClick={(e) => { e.stopPropagation(); onSelect(engine); }}
                className={`w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors border-b border-gray-50 ${selected?.name === engine.name ? 'bg-orange-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm text-gray-900">{engine.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {engine.displacement > 0 && `${engine.displacement} cm³ • `}
                      {engine.power}ch ({engine.power_kw}kW)
                      {engine.engine_code && ` • ${engine.engine_code}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getFuelColor(fuel)}`}>{getFuelLabel(fuel)}</span>
                    {selected?.name === engine.name && <Check size={16} className="text-orange-500" />}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ))
      )}
    </div>
  );
};
